import { fmtDate } from "utils/date";
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Form,
  Spinner,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import classnames from "classnames";
import { CicloService } from "service/CicloService";
import { SalonService } from "service/SalonService";
import { SedeService } from "service/SedeService";
import FSelect from "components/Select/FSelect";
import FDateInput from "components/DateInput/FDateInput";
import CustomPagination from "components/Pagination/Pagination";

const ITEMS_PER_PAGE = 10;

function MantenimientoAcademico() {
  const notificationAlertRef = useRef(null);

  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [activeTab, setActiveTab] = useState("1");
  const [listCiclos, setListCiclos] = useState([]);
  const [listSalones, setListSalones] = useState([]);
  const [listSedes, setListSedes] = useState([]);

  // Search & pagination per tab
  const [searchCiclo, setSearchCiclo] = useState("");
  const [searchSalon, setSearchSalon] = useState("");
  const [searchSede, setSearchSede] = useState("");
  const [pageCiclo, setPageCiclo] = useState(1);
  const [pageSalon, setPageSalon] = useState(1);
  const [pageSede, setPageSede] = useState(1);

  const [loadingSave, setLoadingSave] = useState(false);

  // Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [currentEntity, setCurrentEntity] = useState(null);
  const [formData, setFormData] = useState({});

  const cicloService = new CicloService();
  const salonService = new SalonService();
  const sedeService = new SedeService();

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      if (activeTab === "1") {
        const res = await cicloService.getCiclos();
        setListCiclos(res?.data || []);
      } else if (activeTab === "2") {
        const res = await salonService.getSalones();
        setListSalones(res?.data || []);
      } else if (activeTab === "3") {
        const res = await sedeService.getSedes();
        setListSedes(res?.data || []);
      }
    } catch (error) {
      console.error("Error cargando datos:", error);
    }
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  const handleEdit = (type, data) => {
    setCurrentEntity(type);
    setFormData(data || {});
    setModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setLoadingSave(true);
    try {
      let res;
      if (currentEntity === "ciclo") {
        res = formData._id ? await cicloService.updateCiclo(formData) : await cicloService.addCiclo(formData);
      } else if (currentEntity === "salon") {
        res = formData._id ? await salonService.updateSalon(formData) : await salonService.addSalon(formData);
      } else if (currentEntity === "sede") {
        res = formData._id ? await sedeService.updateSede(formData) : await sedeService.addSede(formData);
      }

      if (res?.confirmation === "success") {
        const accion = formData._id ? "actualizado" : "registrado";
        notify("success", `${currentEntity.charAt(0).toUpperCase() + currentEntity.slice(1)} ${accion} correctamente.`);
        toggleModal();
        loadData();
      } else {
        notify("warning", res?.message || "No se pudo completar la operación.");
      }
    } catch (error) {
      console.error("Error al guardar:", error);
      notify("danger", "Error al guardar. Verifica los datos e intenta nuevamente.");
    } finally {
      setLoadingSave(false);
    }
  };

  // Filtered lists
  const filteredCiclos = listCiclos.filter(c =>
    (c.denominacion || "").toLowerCase().includes(searchCiclo.toLowerCase()) ||
    (c.estado || "").toLowerCase().includes(searchCiclo.toLowerCase())
  );
  const filteredSalones = listSalones.filter(s =>
    (s.denominacion || "").toLowerCase().includes(searchSalon.toLowerCase()) ||
    (s.estado || "").toLowerCase().includes(searchSalon.toLowerCase())
  );
  const filteredSedes = listSedes.filter(s =>
    (s.denominacion || "").toLowerCase().includes(searchSede.toLowerCase()) ||
    (s.direccion || "").toLowerCase().includes(searchSede.toLowerCase())
  );

  // Paginated slices
  const slicePage = (list, page) => list.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const ciclosPage = slicePage(filteredCiclos, pageCiclo);
  const salonesPage = slicePage(filteredSalones, pageSalon);
  const sedesPage = slicePage(filteredSedes, pageSede);

  const handleSearchCiclo = (e) => { setSearchCiclo(e.target.value); setPageCiclo(1); };
  const handleSearchSalon = (e) => { setSearchSalon(e.target.value); setPageSalon(1); };
  const handleSearchSede = (e) => { setSearchSede(e.target.value); setPageSede(1); };

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <Row className="align-items-center mb-3">
                  <Col md="8">
                    <CardTitle tag="h4">Mantenimiento Académico</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button
                      color="link"
                      className="btn-icon btn-round"
                      title="Configuración"
                    >
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => toggleTab("1")}
                      style={{ cursor: "pointer" }}
                    >
                      Ciclos
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "2" })}
                      onClick={() => toggleTab("2")}
                      style={{ cursor: "pointer" }}
                    >
                      Salones
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "3" })}
                      onClick={() => toggleTab("3")}
                      style={{ cursor: "pointer" }}
                    >
                      Sedes
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardHeader>
              <CardBody>
                <TabContent activeTab={activeTab}>
                  {/* Tab Ciclos */}
                  <TabPane tabId="1">
                    {/* Zona 2: Barra de filtros */}
                    <Row className="align-items-end mb-4">
                      <Col md="12">
                        <FormGroup className="mb-0">
                          <Label>Buscar Ciclo</Label>
                          <Input
                            type="text"
                            placeholder="Buscar por denominación o estado..."
                            value={searchCiclo}
                            onChange={handleSearchCiclo}
                            style={{ height: "40px" }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Zona 3: Barra de acciones */}
                    <Row className="mb-4">
                      <Col md="6">
                        {/* Espacio reservado para acciones masivas */}
                      </Col>
                      <Col md="6" className="text-right">
                        <Button
                          color="primary"
                          className="btn-fill"
                          onClick={() => handleEdit("ciclo", { estado: "Habilitado", estadociclopasado: "No" })}
                        >
                          <i className="tim-icons icon-simple-add mr-2" />
                          Agregar Ciclo
                        </Button>
                      </Col>
                    </Row>

                    <hr className="my-4" />
                    <Table responsive>
                      <thead className="text-primary">
                        <tr>
                          <th>Denominación</th>
                          <th>Costo</th>
                          <th>Fechas</th>
                          <th>Estado</th>
                          <th className="text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ciclosPage.map((c, i) => (
                          <tr key={i}>
                            <td>{c.denominacion}</td>
                            <td>S/ {c.costototal}</td>
                            <td>
                              {fmtDate(c.fechainicio)} -{" "}
                              {fmtDate(c.fechafin)}
                            </td>
                            <td>
                              {c.estado === "Habilitado"
                                ? <span className="text-success">{c.estado}</span>
                                : <span className="text-warning">{c.estado}</span>}
                            </td>
                            <td className="text-right">
                              <Button color="info" size="sm" className="btn-icon btn-link" onClick={() => handleEdit("ciclo", c)}>
                                <i className="tim-icons icon-pencil" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {filteredCiclos.length === 0 && (
                          <tr><td colSpan="5" className="text-center">No se encontraron registros</td></tr>
                        )}
                      </tbody>
                    </Table>
                    <CustomPagination
                      itemsPerPage={ITEMS_PER_PAGE}
                      totalItems={filteredCiclos.length}
                      paginate={setPageCiclo}
                      currentPage={pageCiclo}
                    />
                  </TabPane>

                  {/* Tab Salones */}
                  <TabPane tabId="2">
                    {/* Zona 2: Barra de filtros */}
                    <Row className="align-items-end mb-4">
                      <Col md="12">
                        <FormGroup className="mb-0">
                          <Label>Buscar Salón</Label>
                          <Input
                            type="text"
                            placeholder="Buscar por nombre o estado..."
                            value={searchSalon}
                            onChange={handleSearchSalon}
                            style={{ height: "40px" }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Zona 3: Barra de acciones */}
                    <Row className="mb-4">
                      <Col md="6">
                        {/* Espacio reservado para acciones masivas */}
                      </Col>
                      <Col md="6" className="text-right">
                        <Button
                          color="primary"
                          className="btn-fill"
                          onClick={() => handleEdit("salon", { estado: "Activo" })}
                        >
                          <i className="tim-icons icon-simple-add mr-2" />
                          Agregar Salón
                        </Button>
                      </Col>
                    </Row>

                    <hr className="my-4" />
                    <Table responsive>
                      <thead className="text-primary">
                        <tr>
                          <th>Denominación</th>
                          <th>Capacidad</th>
                          <th>Apertura</th>
                          <th>Estado</th>
                          <th className="text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salonesPage.map((s, i) => (
                          <tr key={i}>
                            <td>{s.denominacion}</td>
                            <td>{s.capacidad}</td>
                            <td>{fmtDate(s.fechacreacion)}</td>
                            <td>
                              {s.estado === "Activo"
                                ? <span className="text-success">Activo</span>
                                : <span className="text-danger">Inactivo</span>}
                            </td>
                            <td className="text-right">
                              <Button color="info" size="sm" className="btn-icon btn-link" onClick={() => handleEdit("salon", s)}>
                                <i className="tim-icons icon-pencil" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {filteredSalones.length === 0 && (
                          <tr><td colSpan="5" className="text-center">No se encontraron registros</td></tr>
                        )}
                      </tbody>
                    </Table>
                    <CustomPagination
                      itemsPerPage={ITEMS_PER_PAGE}
                      totalItems={filteredSalones.length}
                      paginate={setPageSalon}
                      currentPage={pageSalon}
                    />
                  </TabPane>

                  {/* Tab Sedes */}
                  <TabPane tabId="3">
                    {/* Zona 2: Barra de filtros */}
                    <Row className="align-items-end mb-4">
                      <Col md="12">
                        <FormGroup className="mb-0">
                          <Label>Buscar Sede</Label>
                          <Input
                            type="text"
                            placeholder="Buscar por nombre o dirección..."
                            value={searchSede}
                            onChange={handleSearchSede}
                            style={{ height: "40px" }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Zona 3: Barra de acciones */}
                    <Row className="mb-4">
                      <Col md="6">
                        {/* Espacio reservado para acciones masivas */}
                      </Col>
                      <Col md="6" className="text-right">
                        <Button
                          color="primary"
                          className="btn-fill"
                          onClick={() => handleEdit("sede", { estado: "Activo" })}
                        >
                          <i className="tim-icons icon-simple-add mr-2" />
                          Agregar Sede
                        </Button>
                      </Col>
                    </Row>

                    <hr className="my-4" />
                    <Table responsive>

                      <thead className="text-primary">
                        <tr>
                          <th>Nombre</th>
                          <th>Dirección</th>
                          <th>Estado</th>
                          <th className="text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sedesPage.map((s, i) => (
                          <tr key={i}>
                            <td>{s.denominacion}</td>
                            <td>{s.direccion}</td>
                            <td>
                              {s.estado === "Habilitado"
                                ? <span className="text-success">Habilitado</span>
                                : <span className="text-danger">Deshabilitado</span>}
                            </td>
                            <td className="text-right">
                              <Button color="info" size="sm" className="btn-icon btn-link" onClick={() => handleEdit("sede", s)}>
                                <i className="tim-icons icon-pencil" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {filteredSedes.length === 0 && (
                          <tr><td colSpan="4" className="text-center">No se encontraron registros</td></tr>
                        )}
                      </tbody>
                    </Table>
                    <CustomPagination
                      itemsPerPage={ITEMS_PER_PAGE}
                      totalItems={filteredSedes.length}
                      paginate={setPageSede}
                      currentPage={pageSede}
                    />
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Unified Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal} size="md" backdrop="static">
        <ModalHeader toggle={toggleModal}>
          {formData._id ? "Editar" : "Nuevo"} {currentEntity}
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label>Denominación / Nombre</Label>
                  <Input
                    name="denominacion"
                    value={formData.denominacion || ""}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            {currentEntity === "ciclo" && (
              <>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Costo Total</Label>
                      <Input type="number" name="costototal" value={formData.costototal || ""} onChange={handleInputChange} />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Estado</Label>
                      <FSelect
                        name="estado"
                        value={formData.estado || ""}
                        onChange={handleInputChange}
                        options={[
                          { value: "Habilitado", label: "Habilitado" },
                          { value: "Deshabilitado", label: "Deshabilitado" },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Mostrar en ciclos pasados</Label>
                      <FSelect
                        name="estadociclopasado"
                        value={formData.estadociclopasado || ""}
                        onChange={handleInputChange}
                        options={[
                          { value: "Si", label: "Sí" },
                          { value: "No", label: "No" },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Fecha Inicio</Label>
                      <FDateInput name="fechainicio" value={formData.fechainicio ? formData.fechainicio.split("T")[0] : ""} onChange={handleInputChange} />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Fecha Fin</Label>
                      <FDateInput name="fechafin" value={formData.fechafin ? formData.fechafin.split("T")[0] : ""} onChange={handleInputChange} />
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}
            {currentEntity === "salon" && (
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Capacidad</Label>
                    <Input type="number" name="capacidad" value={formData.capacidad || ""} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Estado</Label>
                    <FSelect
                      name="estado"
                      value={formData.estado || ""}
                      onChange={handleInputChange}
                      options={[
                        { value: "Activo", label: "Activo" },
                        { value: "Inactivo", label: "Inactivo" },
                      ]}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
            {currentEntity === "sede" && (
              <Row>
                <Col md="8">
                  <FormGroup>
                    <Label>Dirección</Label>
                    <Input name="direccion" value={formData.direccion || ""} onChange={handleInputChange} />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Estado</Label>
                    <FSelect
                      name="estado"
                      value={formData.estado || "Habilitado"}
                      onChange={handleInputChange}
                      options={[
                        { value: "Habilitado", label: "Habilitado" },
                        { value: "Deshabilitado", label: "Deshabilitado" },
                      ]}
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal} disabled={loadingSave}>Cancelar</Button>
          <Button color="primary" onClick={handleSave} disabled={loadingSave}>
            {loadingSave ? <><Spinner size="sm" className="mr-1" />Guardando...</> : "Guardar"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default MantenimientoAcademico;
